/**
 * Raw design tokens. These are the only place literal colors are allowed to
 * live; everything else consumes them through the MUI theme.
 *
 * The primary ramp is derived from the CCI brand blue (#3f6385, hsl(209 36% 38%)),
 * which sits at step 600 so it stays the anchor of the identity while the
 * surrounding steps give us hover/selected/border states that used to be
 * hand-picked per stylesheet.
 */

export const brand = {
  50: "#f4f7fb",
  100: "#e5edf5",
  200: "#c9d9e8",
  300: "#a1bad4",
  400: "#6b91b8",
  500: "#4a77a1",
  600: "#3f6385",
  700: "#2e4e6b",
  800: "#213a54",
  900: "#16273b",
} as const;

export const neutral = {
  50: "#f9fafb",
  100: "#f3f5f7",
  200: "#e4e8ec",
  300: "#ced4da",
  400: "#9ba6b0",
  500: "#71808e",
  600: "#576370",
  700: "#404c59",
  800: "#29323d",
  900: "#192029",
  950: "#11151d",
} as const;

/**
 * Dark mode gets its own surface ladder. Reusing the `neutral` ramp above made
 * every dark surface read as navy-on-navy, and its two darkest steps sat close
 * enough together that cards never separated from the page behind them. These
 * are near-neutral with only a trace of cool, so `brand` is the only thing in
 * dark mode carrying colour.
 */
export const darkSurface = {
  /** Page background. */
  canvas: "#15181c",
  /** Cards, app bar, drawer, grid body. */
  paper: "#1c2025",
  /** One step up: grid headers and input fields. */
  raised: "#23282e",
  /** Menus, popovers and tooltips floating above everything else. */
  overlay: "#2a2f36",
  /**
   * Borders do the work shadows cannot: every entry in the elevation ramp is a
   * near-black shadow, which is invisible against a near-black page.
   */
  border: "#333a42",
} as const;

/** Dark-mode foreground ramp, measured against `darkSurface.paper`. */
export const darkText = {
  /** 13.7:1 */
  primary: "#e8ebef",
  /** 8.7:1 */
  secondary: "#b6bec7",
  /** 3.8:1, up from the 2.3:1 that made placeholder em-dashes vanish. */
  disabled: "#727b85",
} as const;

export const success = {
  light: "#4caf50",
  main: "#2f7d32",
  dark: "#1b5e20",
  contrastText: "#ffffff",
  darkMode: "#81c784",
} as const;

export const warning = {
  light: "#ffb74d",
  main: "#b26a00",
  dark: "#7f4d00",
  contrastText: "#ffffff",
  darkMode: "#fbbf24",
} as const;

export const danger = {
  light: "#ef5350",
  main: "#c62828",
  dark: "#8e1f1f",
  contrastText: "#ffffff",
  darkMode: "#f28b82",
} as const;

export const info = {
  light: "#4fc3f7",
  main: "#0277bd",
  dark: "#01579b",
  contrastText: "#ffffff",
  darkMode: "#4fc3f7",
} as const;

/** 8px base grid. MUI multiplies this by the numeric `spacing` values. */
export const SPACING_UNIT = 8;

export const RADIUS = {
  sm: 6,
  md: 10,
  lg: 14,
  pill: 999,
} as const;

/** Width of the app shell navigation drawer in each state. */
export const DRAWER_WIDTH = 264;
export const DRAWER_WIDTH_COLLAPSED = 72;
